export interface ExtractedProfileViewModel {
  narrative: string;
  stats: {
    positionsCount: number;
    educationsCount: number;
    positionsWithDescription: number;
    skillsCount: number;
    hasSummary: boolean;
  };
  source: "linkedin" | "free-text" | "none";
}

export interface ExtractedProfilePanelProps {
  profile: ExtractedProfileViewModel;
}

function sourceLabel(source: ExtractedProfileViewModel["source"]): string {
  if (source === "linkedin") return "Apify · LinkedIn";
  if (source === "free-text") return "Texto livre";
  return "Sem fonte de perfil";
}

/**
 * Audit panel for the candidate evidence the model actually saw. Lets the
 * operator answer "is the model abstaining because the rubric is strict, or
 * because the input was thin?" — surfaces position counts, whether the
 * summary/about section was populated, and how many positions came back with
 * descriptions (the most common gap in scraped LinkedIn data).
 */
export function ExtractedProfilePanel({ profile }: ExtractedProfilePanelProps) {
  const { stats, source, narrative } = profile;

  const isThin =
    source === "linkedin" && (stats.positionsWithDescription === 0 || !stats.hasSummary);

  const chips: { label: string; tone: "muted" | "amber" | "forest" }[] = [];
  if (source === "linkedin") {
    chips.push({
      label: `${stats.positionsCount} posições`,
      tone: stats.positionsCount === 0 ? "amber" : "muted",
    });
    chips.push({
      label: `${stats.positionsWithDescription}/${stats.positionsCount} com descrição`,
      tone: stats.positionsCount > 0 && stats.positionsWithDescription === 0 ? "amber" : "muted",
    });
    chips.push({
      label: stats.hasSummary ? "Resumo presente" : "Sem resumo (about)",
      tone: stats.hasSummary ? "forest" : "amber",
    });
    chips.push({
      label: `${stats.educationsCount} formações · ${stats.skillsCount} skills`,
      tone: "muted",
    });
  } else if (source === "free-text") {
    chips.push({
      label: `${narrative.length.toLocaleString("pt-BR")} caracteres`,
      tone: "muted",
    });
  }

  return (
    <details
      data-testid="extracted-profile-panel"
      className="card"
      style={{
        padding: "16px 24px",
        marginTop: 0,
      }}
    >
      <summary
        style={{
          listStyle: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--r-ink-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Perfil extraído · {sourceLabel(source)}
          </div>
          <div
            style={{
              fontSize: 14,
              color: "var(--r-ink)",
              fontWeight: 600,
              marginTop: 4,
              lineHeight: 1.4,
            }}
          >
            {isThin
              ? "Perfil com poucos dados — confirme antes de usar o resultado"
              : "Dados considerados na avaliação"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {chips.map((c) => (
            <span
              key={c.label}
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 999,
                background:
                  c.tone === "amber"
                    ? "rgba(220, 152, 67, 0.12)"
                    : c.tone === "forest"
                      ? "rgba(34, 139, 34, 0.1)"
                      : "var(--r-sand, #F4F1EA)",
                color:
                  c.tone === "amber"
                    ? "var(--r-amber, #DC9843)"
                    : c.tone === "forest"
                      ? "var(--r-forest, #228B22)"
                      : "var(--r-ink-muted)",
                fontWeight: 600,
              }}
            >
              {c.label}
            </span>
          ))}
        </div>
      </summary>

      <div style={{ marginTop: 16 }}>
        {narrative ? (
          <pre
            style={{
              fontSize: 12,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              color: "var(--r-ink)",
              background: "var(--r-sand, #F4F1EA)",
              padding: "12px 14px",
              borderRadius: 6,
              maxHeight: 480,
              overflow: "auto",
              margin: 0,
            }}
            data-testid="extracted-profile-narrative"
          >
            {narrative}
          </pre>
        ) : (
          <div
            style={{
              fontSize: 13,
              color: "var(--r-ink-muted)",
              fontStyle: "italic",
              padding: "12px 14px",
            }}
          >
            Nenhuma evidência extraída — verifique se a URL ou texto foi enviado corretamente.
          </div>
        )}
      </div>
    </details>
  );
}
